'use client';

import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFilterParamObjects,
  setSelectedFilterItems,
  setSelectedColors,
  resetProductFilters,
  updateFilters,
} from '@/redux/reducers/search-reducer';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { cn } from '@/utils/cn';
import Button from '@/app/components/Button';
import ProductFilterPrice from '@/app/components/Product/ProductFilterPrice';
import ProductFilterColor from '@/app/components/Product/ProductFilterColor';
import ProductFilterOptionsWrapper from '@/app/components/Product/ProductFilterOptionsWrapper';
import ProductFilterButtons from '@/app/components/Product/ProductFilterButtons';
import { fetchSearchResult } from '@/redux/thunk/search-thunk';

function SearchProductListingFilter({ onView, onClose, className = '' }) {
  const dispatch = useDispatch();
  const { filterKey, selectedFilterItems, selectedColors, sortingFilter } =
    useSelector((state) => state.search);
  const [filterData, setFilterData] = useState([]);
  const [colors, setColors] = useState(null);
  const [initialPriceRange, setInitialPriceRange] = useState([0, 0]);
  const filterRef = useRef(null);
  const [price, setPrice] = useState([0, 0]);
  const [openFilter, setOpenFilter] = useState(onView);
  const [isResetDisabled, setIsResetDisabled] = useState(true);
  const [collapsedItems, setCollapsedItems] = useState(null);
  const [currentURL, setCurrentURL] = useState('');
  const [valueChanged, setValueChanged] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query');

  const setIsChecked = (subItem) => {
    let flag = false;
    if (selectedFilterItems?.length) {
      selectedFilterItems?.map((selectedItem) => {
        if (
          selectedItem.key === subItem.key &&
          selectedItem.id === subItem.id
        ) {
          flag = true;
          return flag;
        }
        return false;
      });
    }
    return flag;
  };

  const FilterDataInit = () => {
    // set Price Range
    if ((filterKey?.priceRange?.start ?? 0) > 0) {
      if (
        Number(filterKey?.priceRange?.start) <
        Number(filterKey?.priceRange?.end)
      ) {
        setInitialPriceRange([
          Number(filterKey?.priceRange?.start),
          Number(filterKey?.priceRange?.end),
        ]);
        setPrice([
          Number(filterKey?.priceRange?.start),
          Number(filterKey?.priceRange?.end),
        ]);
      }
    }
    if (filterKey?.filterOptions?.length) {
      const updatedData = filterKey.filterOptions?.map((item, index) => ({
        ...item,
        filterCollapsed: `${item?.name?.en?.toLowerCase() ?? ''}_${index + 1}`,
        data: item.data?.map((subItem) => ({
          ...subItem,
          nameEn: item.name?.en ?? '',
          isChecked: setIsChecked(subItem),
        })),
      }));

      // Reorder the array so that the 'Color' object comes first
      const colorObject = updatedData.filter((item) => item?.is_color === true);
      const otherObjects = updatedData.filter(
        (item) => !item?.is_color === true,
      );

      const selectedColorTemp = [];
      if ([...colorObject]?.length > 0) {
        colorObject?.forEach((item) => {
          item?.data?.forEach((subItem) => {
            if (subItem.isChecked) {
              selectedColorTemp.push(subItem?.name?.en);
            }
          });
        });
      }
      if (selectedColorTemp?.length > 0) {
        dispatch(setSelectedColors([...selectedColorTemp]));
      }
      setColors([...colorObject]);

      setFilterData([...otherObjects]);
    }
    if (selectedFilterItems?.length > 0) {
      // Filter objects with key "filter_price_range"
      const priceRangeFilters = selectedFilterItems.filter(
        (item) => item.key === 'filter_price_range',
      );
      if (priceRangeFilters?.length > 0) {
        // Find min and max id values
        const { min, max } = (priceRangeFilters ?? []).reduce(
          (acc, item) => {
            acc.min = Math.min(acc.min, item.id);
            acc.max = Math.max(acc.max, item.id);
            return acc;
          },
          { min: 30000000000, max: -30000000000 },
        );
        setPrice([min, max]);
      }
    }
  };

  const handleClose = () => {
    setOpenFilter(false);
    setTimeout(() => {
      onClose(false);
    }, 200);
  };

  const isPriceChanged = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    return !arr1.every((value, index) => value === arr2[index]);
  };

  useEffect(() => {
    let priceChanged = false;
    if (price.length && initialPriceRange.length) {
      priceChanged = isPriceChanged(price, initialPriceRange);
    }
    if (filterData?.length || colors?.length) {
      const allEmpty = filterData?.some((category) =>
        category.data.some((item) => item.isChecked),
      );

      const activeColor = colors?.some((category) =>
        category.data.some((item) => item.isChecked),
      );

      if (activeColor || allEmpty || priceChanged) {
        setIsResetDisabled(false);
      } else {
        setIsResetDisabled(true);
      }
    } else {
      setIsResetDisabled(true);
    }
  }, [colors, filterData, price]);

  useEffect(() => {
    // set filter key for color and others specifications
    if (filterKey?.filterOptions?.length) {
      FilterDataInit();
    }
  }, [filterKey, pathname, currentURL, selectedFilterItems]);

  useEffect(() => {
    if (filterData?.length) {
      setCollapsedItems(
        filterData.reduce((acc, filter) => {
          acc[filter.filterCollapsed] = false;
          return acc;
        }, {}),
      );
    }
  }, [filterData]);

  useEffect(() => {
    if (onView) {
      setTimeout(() => {
        setOpenFilter(true);
        document.body.style.overflow = 'hidden';
      }, 100);
    } else {
      setOpenFilter(false);
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [onView]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (openFilter) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openFilter]);

  const handleCollapse = (filterId) => {
    setCollapsedItems((prevState) => ({
      ...prevState,
      [filterId]: !prevState[filterId],
    }));
  };

  const handleSelect = (itemSelected) => {
    // creating a copy of the store obj
    const selectedItemsTemp = [...filterData];
    const newFilter = selectedItemsTemp.map((item, index) => ({
      ...item,
      data: item?.data?.map((subItem) =>
        subItem.key === itemSelected.key &&
        subItem.id === itemSelected.id &&
        subItem.nameEn === itemSelected.nameEn
          ? { ...subItem, isChecked: !subItem.isChecked }
          : subItem,
      ),
    }));
    setFilterData([...newFilter]);
  };

  const handleFilterReset = () => {
    setValueChanged(false);
    setPrice([
      Number(filterKey?.priceRange?.start),
      Number(filterKey?.priceRange?.end),
    ]);

    const params = new URLSearchParams();
    params.set('query', searchQuery);

    params.set('page', 1);
    params.set('limit', 12);
    const url = `${pathname}?${params.toString()}`;
    setCurrentURL(url);

    dispatch(resetProductFilters());
    dispatch(setSelectedColors([]));

    dispatch(
      updateFilters({
        search: searchQuery,
        ...sortingFilter,
      }),
    );

    dispatch(fetchSearchResult({ searchKey: searchQuery }));

    router.push(url);
  };

  const handleApply = () => {
    const checkedItems = filterData.reduce((acc, category) => {
      const items = category.data
        .filter((item) => item.isChecked)
        .map((item) => ({
          key: item.key,
          id: item.id,
          isChecked: item.isChecked,
        }));
      return acc.concat(items);
    }, []);
    const checkedColors = colors.reduce((acc, category) => {
      const items = category.data
        .filter((item) => item.isChecked)
        .map((item) => ({
          key: item.key,
          id: item.id,
          isChecked: item.isChecked,
        }));
      return acc.concat(items);
    }, []);
    if (checkedColors?.length > 0) {
      checkedItems.push(...checkedColors);
    }
    let priceChanged = false;
    if (price.length && initialPriceRange.length) {
      priceChanged = isPriceChanged(price, initialPriceRange);
    }
    if (priceChanged) {
      checkedItems.push({
        key: 'filter_price_range',
        id: price[1],
        isChecked: true,
      });
      checkedItems.push({
        key: 'filter_price_range',
        id: price[0],
        isChecked: true,
      });
    }
    dispatch(setSelectedFilterItems(checkedItems));

    const grouped = checkedItems.reduce((acc, { key, id }) => {
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(id);
      return acc;
    }, {});

    const queryString = Object.entries(grouped)
      .map(([key, ids]) => `${key}=${ids.reverse().join(',')}`)
      .join('&');

    dispatch(setFilterParamObjects(queryString));
    const params = new URLSearchParams();

    params.set('query', searchQuery);

    params.set('page', 1);
    params.set('limit', 12);
    const url = `${pathname}?${params.toString()}&${queryString}`;

    router.push(url);
    handleClose();
  };

  // used to collapsed close by default
  const toggleCollapsed = ({ isOpen }) => {
    setCollapsedItems(
      filterData.reduce((acc, filter) => {
        acc[filter.filterCollapsed] = isOpen;
        return acc;
      }, {}),
    );
  };

  const handleIsChecked = () => {
    if (filterData?.length) {
      return filterData.some((category) =>
        category.data.some((item) => item.isChecked),
      );
    }
    return false;
  };

  // used collapsed mounted
  useEffect(() => {
    if (filterData?.length) {
      toggleCollapsed({ isOpen: !handleIsChecked() });
    }
  }, [filterData]);

  useEffect(() => {
    if (isResetDisabled) {
      if (!handleIsChecked()) {
        handleFilterReset();
      }
    }
  }, [isResetDisabled]);

  return (
    <div
      className={classNames(
        'search-product-listing-filter fixed inset-0 bg-black/30 w-full h-full flex justify-end opacity-50 invisible transition-all duration-100 z-[199]',
        {
          '!opacity-100 !visible': openFilter,
        },
        className,
      )}
    >
      <div
        ref={filterRef}
        className={classNames(
          'w-full max-w-[320px] lg:max-w-[495px] bg-white h-full relative overflow-y-scroll translate-x-full transition-all duration-[400ms] shadow-job-search-filter thin-scrollbar',
          {
            '!translate-x-0': openFilter,
          },
        )}
      >
        <div className="relative h-full overflow-y-auto">
          <div className={cn('h-max px-20 pt-[56px] lg:pt-[114px] pb-[46px]')}>
            <Button
              type="go-back-small"
              onClick={handleClose}
              className={cn('mb-24')}
            />

            <div className={cn('lg:px-20')}>
              {/* Price */}
              <ProductFilterPrice
                initialPriceRange={initialPriceRange}
                setPrice={setPrice}
                price={price}
                valueChanged={valueChanged}
                setValueChanged={setValueChanged}
              />

              {/* Color */}
              <ProductFilterColor
                colors={colors}
                setColors={setColors}
                selectedColors={selectedColors}
                onSelectedColor={(value) => dispatch(setSelectedColors(value))}
              />

              <div className="pb-40">
                {filterData.length
                  ? filterData?.map((filter, index) => {
                      const isLastItem = index === filterData.length - 1;
                      const isCollapsed =
                        collapsedItems?.[filter.filterCollapsed];

                      return (
                        <ProductFilterOptionsWrapper
                          key={`${index}-${filter.count}`}
                          collapsedItems={collapsedItems}
                          filterData={filterData}
                          filter={filter}
                          handleCollapse={handleCollapse}
                          isCollapsed={isCollapsed}
                          handleSelect={handleSelect}
                          isLastItem={isLastItem}
                        />
                      );
                    })
                  : null}
              </div>
            </div>
          </div>

          {/* Buttons for reset apply */}
          <ProductFilterButtons
            isApplyDisabled={isResetDisabled}
            isResetDisabled={isResetDisabled}
            handleFilterReset={handleFilterReset}
            handleApply={handleApply}
            showForCampaign={false}
          />
        </div>
      </div>
    </div>
  );
}

export default SearchProductListingFilter;
